import {
	Component,
	computed,
	effect,
	ElementRef,
	forwardRef,
	input,
	model,
	OnInit,
	output,
	QueryList,
	signal,
	ViewChildren,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
} from '@angular/forms';
import { getUaDevice } from '@context/frontend/native/platform';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import { TuiLoader, TuiSizeXL, TuiSizeXS } from '@taiga-ui/core';

@Component({
	standalone: true,
	imports: [FormsModule, ReactiveFormsModule, TuiAutoFocus, TuiLoader],
	selector: 'ctx-code-input',
	templateUrl: 'code-input.component.html',
	styleUrl: 'code-input.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CodeInputComponent),
			multi: true,
		},
	],
	host: {
		class: 'relative',
		'[attr.size]': 'size()',
		'[attr.device]': 'device',
	},
})
export class CodeInputComponent implements OnInit, ControlValueAccessor {
	@ViewChildren('inputRef') inputRefs!: QueryList<
		ElementRef<HTMLInputElement>
	>;

	readonly length = input.required<number>();
	readonly device = getUaDevice();

	readonly autoSubmit = input<boolean>(true);
	readonly disabledInput = input<boolean>(false);
	readonly loading = input<boolean>(false);
	private readonly disabledState = signal<boolean>(false);
	readonly size = input<TuiSizeXS | TuiSizeXL>('l');

	readonly code = model<string>('');
	readonly codeComplete = output<{ code: string }>();

	readonly disabled = computed(
		() => this.disabledInput() || this.disabledState() || this.loading(),
	);

	readonly codeArray = computed(() => {
		const codeValue = this.code() || '';
		const arr: string[] = [];
		for (let i = 0; i < this.length(); i++) {
			arr.push(codeValue[i] || '');
		}
		return arr;
	});

	readonly nextInputIndex = computed(() => {
		// Find the first empty input after the last filled input
		const codeArray = this.codeArray();
		for (let i = 0; i < codeArray.length; i++) {
			if (!codeArray[i]) {
				return i;
			}
		}
		// All inputs are filled, return the last index
		return codeArray.length - 1;
	});

	constructor() {
		// Sync model changes to form control
		effect(() => {
			this.onChange(this.code());
		});
	}

	ngOnInit() {
		// Focus first input on init if not disabled
		if (!this.disabled()) {
			setTimeout(() => {
				this.focusInput(0);
			}, 0);
		}
	}

	writeValue(value: string): void {
		if (value !== this.code()) {
			this.code.set(value || '');
		}
	}

	onInput(index: number, event: Event) {
		if (this.disabled()) return;

		const input = event.target as HTMLInputElement;
		const value = input.value.replace(/[^A-Z0-9]/gi, '').toUpperCase();

		if (value.length > 1) {
			// Handle paste
			this.handlePaste(value);
			return;
		}

		if (value) {
			this.updateCode(index, value);
			// Move to next input
			if (index < this.length() - 1) {
				this.focusInput(index + 1);
			} else {
				// Last character entered, auto-submit if enabled
				if (this.autoSubmit() && this.code().length === this.length()) {
					this.onTouched();
					// Emit code complete event
					this.codeComplete.emit({ code: this.code() });
				}
			}
		} else {
			this.updateCode(index, '');
		}
	}

	onKeyDown(index: number, event: KeyboardEvent) {
		if (this.disabled()) return;

		const input = event.target as HTMLInputElement;

		if (event.key === 'Backspace') {
			if (input.value) {
				// Clear current input
				this.updateCode(index, '');
			} else if (index > 0) {
				// Move to previous input
				event.preventDefault();
				this.focusInput(index - 1);
			}
		} else if (event.key === 'ArrowLeft' && index > 0) {
			event.preventDefault();
			this.focusInput(index - 1);
		} else if (event.key === 'ArrowRight' && index < this.length() - 1) {
			event.preventDefault();
			this.focusInput(index + 1);
		} else if (event.key === 'Delete') {
			this.updateCode(index, '');
		}
	}

	onFocus(index: number) {
		if (this.disabled()) return;

		// If this is a programmatic focus (auto-focus), allow it
		if (this.isProgrammaticFocus) {
			const input = this.inputRefs?.get(index)?.nativeElement;
			if (input) {
				input.select();
			}
			return;
		}

		// Check if the focused input is empty
		const currentValue = this.codeArray()[index];
		const nextIndex = this.nextInputIndex();

		// If empty and not the next expected input, refocus to the next unfilled input in sequence
		if (!currentValue && index !== nextIndex) {
			this.focusInput(nextIndex);
			return;
		}

		// Select all text when focused
		const input = this.inputRefs?.get(index)?.nativeElement;
		if (input) {
			input.select();
		}
	}

	onPaste(event: ClipboardEvent) {
		if (this.disabled()) return;

		event.preventDefault();
		const pastedText = event.clipboardData?.getData('text') || '';
		const cleaned = pastedText.replace(/[^A-Z0-9]/gi, '').toUpperCase();
		this.handlePaste(cleaned);
	}

	private handlePaste(value: string) {
		const trimmed = value.substring(0, this.length());
		let newCode = '';
		for (let i = 0; i < this.length(); i++) {
			newCode += trimmed[i] || '';
		}
		this.code.set(newCode);

		// Focus the next empty input or the last one
		const nextIndex = Math.min(trimmed.length, this.length() - 1);
		setTimeout(() => {
			this.focusInput(nextIndex);
			if (this.autoSubmit() && newCode.length === this.length()) {
				// Emit code complete event
				this.codeComplete.emit({ code: newCode });
			}
		}, 0);
	}

	private updateCode(index: number, char: string) {
		const current = this.code() || '';
		const arr = current.split('');
		arr[index] = char;
		const newCode = arr.join('').substring(0, this.length());
		this.code.set(newCode);
	}

	private isProgrammaticFocus = false;

	private focusInput(index: number, isProgrammatic = true) {
		this.isProgrammaticFocus = isProgrammatic;
		setTimeout(() => {
			const input = this.inputRefs?.get(index)?.nativeElement;
			if (input) {
				input.focus();
			}
			// Reset flag after focus
			setTimeout(() => {
				this.isProgrammaticFocus = false;
			}, 0);
		}, 0);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private onChange = (_: string) => {
		//
	};

	private onTouched = () => {
		//
	};

	registerOnChange = (fn: (value: string) => void): void => {
		this.onChange = fn;
	};

	registerOnTouched = (fn: () => void): void => {
		this.onTouched = fn;
	};

	setDisabledState = (isDisabled: boolean): void => {
		this.disabledState.set(isDisabled);
	};
}
